import { ScheduleInterviewSchema, type ScheduleInterviewRequest } from '@aysar/contracts';
import { useTranslation } from 'react-i18next';

import { DynamicFormModal } from '@/shared/components/dynamic-form/DynamicFormModal/DynamicFormModal';
import { type ModalComponentProps } from '@/shared/components/Modals/modal-registry/modal-registry.types';

import { useScheduleInterviewModalLogic } from './useScheduleInterviewModalLogic';

/**
 * Confirming (or moving) the appointment.
 *
 * A `DynamicFormModal` on the **contract's own schema** (W9/U3) — the kit owns
 * the layout, the validation, the server-error mapping and the RTL, and this
 * file supplies the three fields and their words. A bespoke form here would be
 * a second validation path for a value the api is the authority on.
 *
 * The title changes when there is already an appointment: confirming a request
 * and moving a booking are different acts to the member, who is told about both.
 */
export function ScheduleInterviewModal({ open, onCancel, modalData }: ModalComponentProps) {
  const { t } = useTranslation(['verification', 'common']);
  const view = useScheduleInterviewModalLogic(modalData, onCancel);

  if (view.caseId === null) {
    return null;
  }

  return (
    <DynamicFormModal<ScheduleInterviewRequest>
      isOpen={open}
      onOpenChange={(next) => {
        if (!next) onCancel();
      }}
      title={t(
        view.isRescheduling
          ? 'verification:interview.action.reschedule'
          : 'verification:interview.schedule.title',
      )}
      description={t('verification:interview.schedule.subtitle')}
      schema={ScheduleInterviewSchema}
      fields={view.fields}
      initialValues={view.initialValues}
      onSubmit={view.submit}
      onCancel={onCancel}
      submitLabel={t('verification:interview.schedule.submit')}
      cancelLabel={t('common:action.cancel')}
    />
  );
}
